import { IDecision } from '../interfaces/IDecision';
import { IRuleResults } from '../interfaces/IRuleResults';

/** Internal Maestro taxonomy strings — used for bucket classification, not UW display. */
export const HIDDEN_DECISION_CHANGE_REASONS = new Set([
  'Application RUW triggered due to rules',
  'Application RUW triggered due to missing evidence',
  'Application RUW triggered due to failed evidence',
]);

export function filterVisibleDecisionChangeReasons(reasons: string[]): string[] {
  return reasons.filter((reason) => !HIDDEN_DECISION_CHANGE_REASONS.has(reason));
}

/**
 * True when any matched rule across any ruleSet carries the Sahara
 * `sahara_decline_to_ruw` requirement — indicating decline-eligible rules
 * were re-classified to RUW for that product. Callers use this to emit
 * the "originally declining" message in {@link getMessageFromDecision}.
 */
export function isSaharaDeclineToRuw(
  ruleResults: IRuleResults | undefined,
): boolean {
  return !!ruleResults?.ruleSets?.some(ruleSet =>
    ruleSet.matchedRules.some(rule =>
      rule.requirement?.includes('sahara_decline_to_ruw'),
    ),
  );
}

/**
 * Derive the human-readable list of "why was this case referred to underwriting"
 * messages from a decision's calculationInfo. Used by RuleResults to surface
 * the messages inline, and by the RuwReasons workbench section to populate
 * top-of-page reason cards.
 *
 * `saharaDeclineToRuw` is a product-specific flag callers may opt in to —
 * pass `false` when the caller wants product-agnostic messages.
 */
export function getMessageFromDecision(
  decision: IDecision | undefined,
  saharaDeclineToRuw: boolean,
): string[] {
  const messageArray: string[] = [];
  if (!decision) return messageArray;
  const calculationInfo = decision.calculationInfo;

  if (calculationInfo?.decisionChangeReasons) {
    messageArray.push(...filterVisibleDecisionChangeReasons(calculationInfo.decisionChangeReasons));
  } else {
    // for policies before decisionChangeReasons change
    if (Boolean(calculationInfo?.movedToRuwAsReinstatementDeclinedEarlier)) {
      messageArray.push(
        'Application RUW triggered due to previous decline in Reinstatement',
      );
    }
    if (calculationInfo?.ruwDueToWorsePreviousDecision?.reason) {
      messageArray.push(calculationInfo.ruwDueToWorsePreviousDecision.reason);
    }
    if (
      calculationInfo?.eligibilityDowngrades &&
      typeof calculationInfo.eligibilityDowngrades === 'object' &&
      !Array.isArray(calculationInfo.eligibilityDowngrades) &&
      Object.keys(calculationInfo.eligibilityDowngrades).length > 0
    ) {
      messageArray.push('Eligibility Downgrade applies to this application');
    }
  }

  let containsV2Flags = false;
  if (Boolean(calculationInfo?.downgradeDetails?.isClassDowngradedDueToTrl)) {
    messageArray.push('TRL Downgrade applies to this application');
    containsV2Flags = true;
  }
  if (
    Boolean(calculationInfo?.downgradeDetails?.isClassDowngradedDueToBucketing)
  ) {
    messageArray.push('Bucketing logic applies to this application');
    containsV2Flags = true;
  }
  if (
    Boolean(
      calculationInfo?.downgradeDetails
        ?.isClassDowngradedDueToOtherThanTrlAndBucketing,
    )
  ) {
    messageArray.push('Class downgrade applies to this application');
    containsV2Flags = true;
  }
  if (!containsV2Flags && Boolean(calculationInfo?.downgradeDetails?.reason)) {
    messageArray.push(
      'Downgrades or bucketing logic applies to this application',
    );
  }
  if (saharaDeclineToRuw) {
    messageArray.push(
      'This policy contains RUW rule(s) that were originally declining',
    );
  }
  return messageArray;
}
